import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { useOne, useTable, useTranslation, useUpdate } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { _openUrlOnNewTab, isHaveActionPermission } from "utills/helpers";
import { ConfirmModal } from "components/confirmModal";
import { useState } from "react";
import { IEventIdsItem, IPromotionConditionItem } from "interfaces";
import {
  PromotionConditionCard,
  PromotionConditionFilter,
  PromotionConditionTable,
} from "components/shared/promotion/condition";
import { ConditionInfoModal } from "components/shared/promotion/modal";
import { Link } from "react-router-dom";
import { endpointService } from "components/shared/services/endpointService";

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  status: "",
  conditionName: "",
  submit: () => {},
};

const INITIAL_CONDITION_INFO_MODAL_CONFIG: any = {
  open: false,
  data: null,
};

export const WPSPromotionConditionList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();
  const { translate } = useTranslation();
  const [confirmModalConfig, setConfirmModalConfig] = useState(
    INITIAL_ACTION_MODAL_CONFIG
  );

  const [conditionInfoModalConfig, setConditionInfoModalConfig] = useState(
    INITIAL_CONDITION_INFO_MODAL_CONFIG
  );

  const { tableQuery, filters, setFilters } = useTable<IPromotionConditionItem>(
    {
      resource: "promotion/conditions",
      syncWithLocation: true,
      pagination: {
        mode: "off",
      },
      meta: {
        resource: "data",
      },
    }
  );

  const { data: eventsData, isLoading: isLoadingEvents } = useOne<
    IEventIdsItem[]
  >({
    resource: endpointService.getPromotionEndPoint("getPromotionEventDropdown"),
    id: "",
    meta: {
      resource: "data",
    },
  });

  const _toggleActive = (id: number, isActive: boolean) => {
    updateMutation(
      {
        resource: "promotion/conditions",
        id,
        meta: {
          URLSuffix: isActive ? "status/true" : "status/false",
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      }
    );
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap((item) =>
      "field" in item ? item : []
    );
    const pageIndex = updatedFilters.findIndex(
      (filter) => filter.field === "page"
    );
    if (pageIndex < 0) {
      updatedFilters.push({ field: "page", operator: "eq", value: page });
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  };

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      status: confirmModalConfig.status,
      conditionName: confirmModalConfig.conditionName,
    });
  };

  const _onCloseConditionInfoModal = () => {
    setConditionInfoModalConfig({
      ...INITIAL_CONDITION_INFO_MODAL_CONFIG,
      data: conditionInfoModalConfig.data,
    });
  };

  if (tableQuery.isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="wrapper p-4">
      <Breadcrumbs />
      {isHaveActionPermission("coupon", "promotion", "create") ? (
        <div className="w-full text-sm px-4 sm:px-6">
          <p>
            Want to create new promotion?{" "}
            <Link to={"/promotion/coupon/create/new?redirect=condition"}>
              <span className="text-brand-500 font-medium hover:underline">
                Create Now
              </span>
            </Link>
          </p>
        </div>
      ) : (
        <></>
      )}
      <div className="box-container">
        <PromotionConditionFilter
          filters={filters}
          setFilters={setFilters}
          events={eventsData?.data ?? []}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length && !tableQuery.isFetching && (
          <div className="text-center">No data</div>
        )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <PromotionConditionCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={(id, isActive, conditionName) =>
                  setConfirmModalConfig({
                    open: true,
                    status: isActive ? "activate" : "deactivate",
                    conditionName: conditionName,
                    submit: () => _toggleActive(id, isActive),
                  })
                }
                _showInfoModal={(data: IPromotionConditionItem) =>
                  setConditionInfoModalConfig({
                    open: true,
                    data: data,
                  })
                }
              />
            </div>
            <div className="hidden sm:block">
              <PromotionConditionTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={(id, isActive, conditionName) =>
                  setConfirmModalConfig({
                    open: true,
                    status: isActive ? "activate" : "deactivate",
                    conditionName: conditionName,
                    submit: () => _toggleActive(id, isActive),
                  })
                }
                _showInfoModal={(data: IPromotionConditionItem) =>
                  setConditionInfoModalConfig({
                    open: true,
                    data: data,
                  })
                }
              />
            </div>
          </>
        ) : (
          <></>
        )}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_item ?? 0,
                total: tableQuery?.data?.paging.total,
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {tableQuery.isLoading ||
      (tableQuery.isFetching && tableQuery.isPreviousData) ||
      updateMutationIsLoading ? (
        <FullScreenLoading />
      ) : (
        <></>
      )}
      <ConfirmModal
        key="confirm-modal"
        title=""
        content=""
        customContent={
          <div className="mb-4 text-center text-lg font-medium">
            {translate("modal.toggleConditionStatus", {
              status: translate(confirmModalConfig.status),
              conditionName: confirmModalConfig.conditionName
            })}
          </div>
        }
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
      />
      <ConditionInfoModal
        onClose={() => _onCloseConditionInfoModal()}
        open={conditionInfoModalConfig.open}
        conditionData={conditionInfoModalConfig.data}
        events={eventsData?.data ?? []}
      />
    </div>
  );
};
